import React, { useEffect, useState } from 'react';
import { Loader2, Search, ChevronRight, ChevronDown } from 'lucide-react';
import {
  Button,
  Checkbox,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { getInterventionOptionsForVisit } from '../../services/api';
import type { InterventionTemplateDetail } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { renderInstructionFields } from '../../utils/goals';

interface InterventionOptionCardProps {
  option: InterventionTemplateDetail;
  selected: boolean;
  onClick: () => void;
}

const InterventionOptionCard: React.FC<InterventionOptionCardProps> = ({
  option,
  selected,
  onClick,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => !prev);
  };

  return (
    <Card
      className={`cursor-pointer border transition-colors ${
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
          : 'hover:bg-muted/50'
      }`}
      onClick={onClick}
    >
      <CardContent className='px-2 py-1'>
        <div className='flex items-center gap-1.5'>
          <Checkbox checked={selected} className='mt-0.5 pointer-events-none' />
          <p className='text-sm font-medium leading-tight flex-1'>
            {option.name}
          </p>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  onClick={handleChevronClick}
                  className='h-5 w-5 p-0 text-muted-foreground hover:text-foreground shrink-0'
                  aria-expanded={expanded}
                  aria-label={expanded ? 'Collapse details' : 'Expand details'}
                >
                  {expanded ? (
                    <ChevronDown className='h-4 w-4' />
                  ) : (
                    <ChevronRight className='h-4 w-4' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {expanded ? 'Hide details' : 'Show details'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {expanded && (
          <div className='mt-2 pl-7 text-sm text-muted-foreground'>
            {renderInstructionFields(option.instruction)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface AddInterventionsBoxProps {
  open: boolean;
  onClose: () => void;
  visitId: string;
  onAddInterventions?: (templates: InterventionTemplateDetail[]) => void;
  onUseFreeText?: () => void;
}

const AddInterventionsBox: React.FC<AddInterventionsBoxProps> = ({
  open,
  onClose,
  visitId,
  onAddInterventions,
  onUseFreeText,
}) => {
  const [interventionOptions, setInterventionOptions] = useState<
    InterventionTemplateDetail[]
  >([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { error: showError } = useToast();

  // Resets all component state when the dialog is closed. IMPORTANT: When adding
  // new state variables, add them to this function so the component starts fresh
  // each time it's opened.
  const resetState = () => {
    setInterventionOptions([]);
    setSelectedIds(new Set());
    setSearchQuery('');
    setIsLoading(false);
    setHasError(false);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    setSelectedIds(new Set());
    setSearchQuery('');
    setHasError(false);
    setIsLoading(true);
    getInterventionOptionsForVisit({
      path: { visit_id: visitId },
      throwOnError: true,
    })
      .then(response => {
        setHasError(false);
        setInterventionOptions(response.data ?? []);
      })
      .catch(() => {
        setHasError(true);
        showError(
          'Failed to load intervention options',
          'Please close and try again'
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [open, visitId]);

  const filteredInterventionOptions = interventionOptions.filter(option =>
    option.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleUseFreeText = () => {
    onUseFreeText?.();
    onClose();
  };

  const handleContinue = () => {
    const selected = interventionOptions.filter(opt => selectedIds.has(opt.id));
    onAddInterventions?.(selected);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='max-w-2xl h-[68vh] flex flex-col overflow-hidden p-3 gap-2'>
        <DialogHeader className='flex-shrink-0 space-y-0.5'>
          <DialogTitle className='text-base'>
            Select one or more interventions to add
          </DialogTitle>
          {(isLoading || hasError || interventionOptions.length === 0) && (
            <p className='text-xs text-muted-foreground'>
              {isLoading
                ? 'Loading interventions...'
                : hasError
                  ? 'Please try again. If the issue persists, contact your administrator'
                  : 'There are no interventions available for this visit. Please contact your administrator'}
            </p>
          )}
        </DialogHeader>
        {!isLoading && !hasError && interventionOptions.length > 0 && (
          <div className='relative'>
            <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder='Search interventions...'
              inputSize='sm'
              className='pl-8'
            />
          </div>
        )}
        <div className='flex-1 min-h-0 overflow-y-auto pr-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
          {isLoading ? (
            <div className='flex-1 flex items-center justify-center'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          ) : hasError ? null : interventionOptions.length > 0 ? (
            <div className='space-y-1.5 pr-1'>
              {filteredInterventionOptions.map(option => (
                <InterventionOptionCard
                  key={option.id}
                  option={option}
                  selected={selectedIds.has(option.id)}
                  onClick={() => toggleSelection(option.id)}
                />
              ))}
            </div>
          ) : null}
          {!isLoading &&
            !hasError &&
            interventionOptions.length > 0 &&
            filteredInterventionOptions.length === 0 && (
              <p className='py-8 text-center text-sm text-muted-foreground'>
                No matching interventions
              </p>
            )}
        </div>
        <DialogFooter className='flex-shrink-0 w-full grid grid-cols-[1fr_auto] items-center'>
          <Button
            variant='outline'
            size='sm'
            className='justify-self-start'
            onClick={handleUseFreeText}
          >
            Use Free Text
          </Button>
          <div className='flex gap-2'>
            <Button variant='outline' size='sm' onClick={onClose}>
              Cancel
            </Button>
            <Button
              size='sm'
              onClick={handleContinue}
              disabled={selectedIds.size === 0}
            >
              Continue ({selectedIds.size})
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddInterventionsBox;
